import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useRouteError } from 'react-router';
import { useMe } from './api/hooks.ts';
import { Empty, Spinner } from './components/ui/index.tsx';
import { ForgotPage, LoginPage, RegisterPage, RequireAuth, ResetPage, VerifyEmailPage } from './features/auth/AuthPages.tsx';
import { MorePage, TreeLayout } from './layout/AppShell.tsx';
import { AccountPage } from './pages/AccountPage.tsx';
import { LandingPage } from './pages/LandingPage.tsx';
import { TreesPage } from './pages/TreesPage.tsx';
import { t } from './i18n/index.ts';

function lazyNamed<T extends Record<string, ComponentType>>(loader: () => Promise<T>, name: keyof T) {
  const C = lazy(async () => ({ default: (await loader())[name] as ComponentType }));
  return function Lazy() {
    return (
      <Suspense fallback={<Spinner />}>
        <C />
      </Suspense>
    );
  };
}

const HomePage = lazyNamed(() => import('./features/home/HomePage.tsx'), 'HomePage');
const TreePage = lazyNamed(() => import('./features/tree/TreePage.tsx'), 'TreePage');
const PersonPage = lazyNamed(() => import('./features/person/PersonPage.tsx'), 'PersonPage');
const ChronologyPage = lazyNamed(() => import('./features/person/ChronologyPage.tsx'), 'ChronologyPage');
const SearchPage = lazyNamed(() => import('./features/search/SearchPage.tsx'), 'SearchPage');
const TimelinePage = lazyNamed(() => import('./features/timeline/TimelinePage.tsx'), 'TimelinePage');
const HistoryPage = lazyNamed(() => import('./features/history/HistoryPage.tsx'), 'HistoryPage');
const AdminPage = lazyNamed(() => import('./features/admin/AdminPage.tsx'), 'AdminPage');
const JoinPage = lazyNamed(() => import('./features/join/JoinPage.tsx'), 'JoinPage');
const MeetPage = lazyNamed(() => import('./features/meet/MeetPage.tsx'), 'MeetPage');
const MeetResultPage = lazyNamed(() => import('./features/meet/MeetResultPage.tsx'), 'MeetResultPage');
const EventsPage = lazyNamed(() => import('./features/events/EventsPage.tsx'), 'EventsPage');
const EventPage = lazyNamed(() => import('./features/events/EventPage.tsx'), 'EventPage');

function Home() {
  const me = useMe();
  if (me.isLoading) return <Spinner />;
  if (!me.data) return <LandingPage />;
  if (me.data.trees.length === 1) return <Navigate to={`/t/${me.data.trees[0]!.id}`} replace />;
  return <Navigate to="/arboles" replace />;
}

function RouteError() {
  const error = useRouteError() as Error | undefined;
  const chunk = /dynamically imported module|Importing a module script failed/i.test(error?.message ?? '');
  return (
    <Empty
      title={chunk ? t('Hay una versión nueva de Gallarday') : t('Algo salió mal en esta pantalla')}
      action={<button className="rounded-full bg-pine px-4 py-2 text-on-pine" onClick={() => window.location.reload()}>{t('Recargar')}</button>}
    >
      {chunk ? t('Recarga la página para continuar.') : t('Recarga la página. Si el problema continúa, avisa a quien administra el árbol.')}
    </Empty>
  );
}

function NotFound() {
  return <Empty title={t('Esta página no existe')} action={<a className="underline" href="/">{t('Ir al inicio')}</a>} />;
}

const auth = (el: React.ReactNode) => <RequireAuth>{el}</RequireAuth>;

const router = createBrowserRouter([
  {
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/entrar', element: <LoginPage /> },
      { path: '/registro', element: <RegisterPage /> },
      { path: '/olvide', element: <ForgotPage /> },
      { path: '/restablecer/:token', element: <ResetPage /> },
      { path: '/verificar/:token', element: <VerifyEmailPage /> },
      { path: '/unirse/:token', element: <JoinPage /> },
      { path: '/encuentro/:token', element: auth(<MeetResultPage />) },
      { path: '/arboles', element: auth(<TreesPage />) },
      { path: '/cuenta', element: auth(<AccountPage />) },
      {
        path: '/t/:treeId',
        element: auth(<TreeLayout />),
        children: [
          { index: true, element: <HomePage /> },
          { path: 'arbol', element: <TreePage /> },
          { path: 'persona/:personId', element: <PersonPage /> },
          { path: 'persona/:personId/cronologia', element: <ChronologyPage /> },
          { path: 'buscar', element: <SearchPage /> },
          { path: 'cronologia', element: <TimelinePage /> },
          { path: 'historial', element: <HistoryPage /> },
          { path: 'encuentro', element: <EventsPage /> },
          { path: 'encuentro/codigo', element: <MeetPage /> },
          // Dirección anterior de la lista de eventos (enlaces ya compartidos).
          { path: 'encuentro/eventos', element: <Navigate to=".." relative="path" replace /> },
          { path: 'encuentro/eventos/:eventId', element: <EventPage /> },
          { path: 'admin', element: <AdminPage /> },
          { path: 'mas', element: <MorePage /> },
        ],
      },
      { path: '*', element: <NotFound /> },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
